import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../utils/api";
import Logo from "../../assets/logo.jpg";

export default function DLRegister() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "doctor",
    termsAccepted: false,
  });
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("form"); // "form" | "otp"
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.termsAccepted) {
      toast.error("You must agree with the terms and conditions");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      toast.info("OTP sent to your phone number");
      setStep("otp");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/verify-otp", { phone: form.phone, otp });
      toast.success("Phone verified! You can now log in.");
      navigate("/login/doctor-lab");
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "OTP verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Brand / Logo */}
        <div className="text-center mb-8 flex flex-col items-center">
          <Link to="/" className="mb-2">
            <img
              src={Logo}
              alt="Docjee"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <p className="text-slate-500 mt-1 text-sm">Doctor & Lab Portal — Professional Registration</p>
        </div>

        <div className="card bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
          {step === "form" ? (
            <>
              <h2 className="font-display font-semibold text-xl text-slate-800 mb-6">
                Professional Registration
              </h2>
              <form onSubmit={handleRegister} className="space-y-4">
                {/* Role Radio Group */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Register as
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={`flex items-center justify-center px-4 py-3 rounded-2xl border cursor-pointer text-sm font-medium transition ${
                        form.role === "doctor"
                          ? "border-emerald-600 bg-emerald-50/60 text-emerald-700 shadow-sm"
                          : "border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value="doctor"
                        checked={form.role === "doctor"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      Doctor
                    </label>
                    <label
                      className={`flex items-center justify-center px-4 py-3 rounded-2xl border cursor-pointer text-sm font-medium transition ${
                        form.role === "lab"
                          ? "border-emerald-600 bg-emerald-50/60 text-emerald-700 shadow-sm"
                          : "border-slate-200 bg-slate-50/50 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      <input
                        type="radio"
                        name="role"
                        value="lab"
                        checked={form.role === "lab"}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      Lab
                    </label>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Full Name 
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Dr. John Doe / City Diagnostics"
                    required
                    className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                    className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="9876543210"
                    required
                    className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
                    Password
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
                  />
                </div>

                {/* Terms and Conditions Checkbox */}
                <div className="flex items-center space-x-3 pt-1">
                  <input
                    type="checkbox"
                    name="termsAccepted"
                    id="termsAccepted"
                    checked={form.termsAccepted}
                    onChange={handleChange}
                    required
                    className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                  />
                  <label htmlFor="termsAccepted" className="text-xs text-slate-600 cursor-pointer">
                    I agree with the{" "}
                    <Link to="/terms" className="text-emerald-600 hover:underline font-medium">
                      terms and conditions
                    </Link>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mt-2 rounded-full bg-emerald-600 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-70"
                >
                  {loading ? "Registering…" : "Register"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-display font-semibold text-xl text-slate-800 mb-2">
                Verify Phone
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Enter the OTP sent to {form.phone}
              </p>
              <form onSubmit={handleVerify} className="space-y-4">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter 6-digit OTP"
                  maxLength={6}
                  required
                  className="input-field w-full rounded-2xl border border-slate-200 bg-slate-50/50 px-4 py-3 text-center text-2xl tracking-[0.5em] text-slate-700 outline-none focus:border-emerald-600 focus:bg-white"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full rounded-full bg-emerald-600 py-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-70"
                >
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </form>
            </>
          )}

          <div className="mt-6 pt-6 border-t border-slate-100 space-y-3 text-center text-sm text-slate-500">
            <p>
              Already have an account?{" "}
              <Link to="/login/doctor-lab" className="text-emerald-600 hover:underline font-medium">
                Sign in
              </Link>
            </p>
            <div className="flex items-center justify-center gap-4 text-xs font-medium text-slate-600 pt-2">
              <Link to="/" className="hover:text-emerald-600 transition-colors">
                Home
              </Link>
              <span>•</span>
              <Link to="/register/patient" className="hover:text-emerald-600 transition-colors">
                Patient Register
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}